import type { LicensePlate, PickingOrder, SceneCell, TaskDetail, WorkerPosition } from "@/lib/api/map3d";
import type { Layout, Placed, Point } from "./geometry";
import type { CellVisual, PalletMarker, SceneCellItem, WorkerMarker } from "./Scene3D";

/**
 * Server ma'lumotini sahna belgilariga aylantirish — bitta joyda,
 * sahnadan ALOHIDA: three.js kodi faqat chizadi, «xodim qayerda» yoki
 * «pallet kimniki» degan qarorlar shu yerda.
 */

/** Shundan eski amal — xodim belgisi xiralashadi. */
export const STALE_MINUTES = 30;
/** Shundan yangi — «jonli». */
export const LIVE_MINUTES = 5;

export function cellVisual(cell: SceneCell): CellVisual {
  if (!cell.is_active) return "inactive";
  if (cell.blocked) return "blocked";
  const stocked = cell.occupied_slots > 0 || Number(cell.on_hand_qty) > 0;
  if (!stocked) return "empty";
  if (cell.capacity_pallets != null && cell.occupied_slots >= cell.capacity_pallets) return "full";
  return cell.capacity_pallets == null ? "partial" : "full";
}

export function sceneItems(cells: SceneCell[], layout: Layout): SceneCellItem[] {
  const items: SceneCellItem[] = [];
  for (const cell of cells) {
    const placed = layout.locate(cell.code);
    if (!placed) continue;
    items.push({ locationId: cell.location_id, code: cell.code, placed, visual: cellVisual(cell) });
  }
  return items;
}

/** LP → buyurtma (joriy tara va barcha taralar bo'yicha). */
export function ordersByLp(orders: PickingOrder[]): Map<string, PickingOrder> {
  const result = new Map<string, PickingOrder>();
  for (const order of orders) {
    if (order.lp_id) result.set(order.lp_id, order);
    for (const tara of order.taras ?? []) result.set(tara.lp_id, order);
  }
  return result;
}

export interface PalletInfo {
  marker: PalletMarker;
  cell: SceneCell;
  lp?: LicensePlate;
  order?: PickingOrder;
}

/**
 * Hududlardagi palletlar: nazorat, jo'natish, qabul va qaytarish
 * kataklarida turgan har bir LP — alohida belgi. Katakda qoldiq bor,
 * lekin LP ro'yxatda yo'q bo'lsa (masalan, LPsiz qoldiq) — kulrang
 * nomsiz belgi: yuk borligi yashirilmaydi.
 */
export function zonePallets(
  cells: SceneCell[],
  layout: Layout,
  lps: LicensePlate[],
  byLp: Map<string, PickingOrder>,
): PalletInfo[] {
  const lpsAt = new Map<string, LicensePlate[]>();
  for (const lp of lps) {
    if (!lp.current_location_id) continue;
    const list = lpsAt.get(lp.current_location_id) ?? [];
    list.push(lp);
    lpsAt.set(lp.current_location_id, list);
  }
  const result: PalletInfo[] = [];
  for (const cell of cells) {
    const placed = layout.locate(cell.code);
    if (!placed || placed.kind !== "ZONE") continue;
    const here = lpsAt.get(cell.location_id) ?? [];
    const count = Math.max(here.length, cell.occupied_slots, Number(cell.on_hand_qty) > 0 ? 1 : 0);
    for (let slot = 0; slot < count; slot++) {
      const lp = here[slot];
      const order = lp ? byLp.get(lp.id) : undefined;
      const tone: PalletMarker["tone"] = lp?.status === "CONTROL" ? "control" : lp?.status === "SHIPPING" ? "shipping" : "stored";
      result.push({
        marker: {
          key: lp?.id ?? `${cell.location_id}#${slot}`,
          placed,
          slot,
          tone,
          label: order ? order.order_number : lp?.lp_code ?? cell.code,
        },
        cell,
        lp,
        order,
      });
    }
  }
  return result;
}

export interface WorkerInfo {
  position: WorkerPosition;
  marker: WorkerMarker | null;
  placed?: Placed;
  name: string;
  minutes: number | null;
  status: "live" | "idle" | "stale" | "none";
}

export function workerName(worker: WorkerPosition): string {
  return worker.full_name || worker.username;
}

/**
 * Xodimning joyi: oxirgi qadam katagi (ID → kod → joy), bo'lmasa
 * skanerlangan kod, bo'lmasa topshiriq hududining markazi. Hech biri
 * bo'lmasa — xaritada yo'q (ro'yxatda «joy noma'lum»).
 */
export function resolveWorkers(
  positions: WorkerPosition[],
  layout: Layout,
  codeById: Map<string, string>,
  now: number,
): WorkerInfo[] {
  const taken = new Map<string, number>();
  return positions.map((position) => {
    const name = workerName(position);
    const activity = position.last_activity_at ?? position.last?.at;
    const minutes = activity ? Math.max(0, (now - new Date(activity).getTime()) / 60_000) : null;
    const status: WorkerInfo["status"] =
      minutes == null ? "none" : minutes < LIVE_MINUTES ? "live" : minutes < STALE_MINUTES ? "idle" : "stale";

    let placed: Placed | undefined;
    let point: Point | null = null;
    let heading = 0;
    const last = position.last;
    if (last?.location_id) {
      const code = codeById.get(last.location_id);
      placed = code ? layout.locate(code) : undefined;
    }
    if (!placed && last?.scanned_code) placed = layout.locate(last.scanned_code.trim().toUpperCase());
    if (placed) {
      point = { ...placed.access };
      // Kara vilkasi katakka qaragan.
      heading = placed.z >= placed.access.z ? 0 : Math.PI;
    } else if (last?.zone_code) {
      const zone = layout.zoneFor(last.zone_code);
      if (zone) point = { x: zone.rect.x + zone.rect.w / 2, z: zone.rect.z + zone.rect.d * 0.35 };
    }
    let marker: WorkerMarker | null = null;
    if (point) {
      // Bir nuqtada bir nechta xodim — yonma-yon.
      const key = `${Math.round(point.x / 500)}:${Math.round(point.z / 500)}`;
      const index = taken.get(key) ?? 0;
      taken.set(key, index + 1);
      point = { x: point.x + index * 1100, z: point.z };
      marker = {
        id: position.user_id,
        kind: position.kind,
        point,
        heading,
        stale: status === "stale",
        status: status === "none" ? "stale" : status,
        label: position.kind === "forklift" ? `KARA · ${name.toUpperCase()}` : name.toUpperCase(),
        sub: minutes == null ? "—" : minutesLabel(minutes),
      };
    }
    return { position, marker, placed, name, minutes, status };
  });
}

export function minutesLabel(minutes: number): string {
  if (minutes < 1) return "hozir";
  if (minutes < 60) return `${Math.round(minutes)} daq`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)} soat`;
  return `${Math.round(hours / 24)} kun`;
}

/** Satr «hali bajarilmagan»mi — marshrutga kiradi. */
export function openLine(status: string): boolean {
  return status !== "CONFIRMED" && status !== "SHORT" && status !== "CANCELLED";
}

export interface TaskRoute {
  stops: Array<{ point: Point; label: string; code: string }>;
  destination: { point: Point; label: string } | null;
}

/**
 * Joriy topshiriqning qolgan yo'li: bajarilmagan satrlar manbasi
 * (server bergan tartibda — `route_seq`, ya'ni TSD dagi tartib), keyin
 * maqsad: satr maqsad katagi yoki topshiriq hududi (masalan, nazorat).
 */
export function taskRoute(task: TaskDetail | undefined, layout: Layout, codeById: Map<string, string>): TaskRoute {
  const stops: TaskRoute["stops"] = [];
  let destination: TaskRoute["destination"] = null;
  if (!task) return { stops, destination };
  const seen = new Set<string>();
  for (const line of task.lines ?? []) {
    if (!openLine(line.status) || !line.source_location_id) continue;
    const code = codeById.get(line.source_location_id);
    const placed = code ? layout.locate(code) : undefined;
    if (!code || !placed || seen.has(code)) continue;
    seen.add(code);
    stops.push({ point: placed.access, label: `${stops.length + 1} · ${code}`, code });
  }
  const targetId = (task.lines ?? []).find((line) => line.target_location_id)?.target_location_id;
  const targetCode = targetId ? codeById.get(targetId) : undefined;
  const target = targetCode ? layout.locate(targetCode) : undefined;
  if (target && targetCode) {
    destination = { point: target.access, label: `MAQSAD · ${targetCode}` };
  } else if (task.target_zone_code) {
    const zone = layout.zoneFor(task.target_zone_code);
    if (zone) {
      destination = {
        point: { x: zone.rect.x + zone.rect.w / 2, z: zone.rect.z + zone.rect.d / 2 },
        label: `MAQSAD · ${zone.label.toUpperCase()}`,
      };
    }
  } else if (task.task_type === "PICK") {
    // Terish oxiri — nazorat (sozlamadagi depo).
    destination = { point: layout.depot(), label: `MAQSAD · ${layout.config.depot.label.toUpperCase()}` };
  }
  return { stops, destination };
}

/** Metr bilan qisqa yozuv. */
export function metres(mm: number): string {
  return `${Math.round(mm / 1000)} m`;
}
